import TechnicianRow from "./TechnicianRow";

export default function TechnicianTimeline({
  rows,
  hours,
  selectedDate,
  viewMode = "timeline",
  onDateChange,
  onOpen,
  onDropCard,
  onDragStart,
  onViewModeChange,
}) {
  return (
    <section className="rounded-[26px] border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
            Dagplanning
          </p>
          <h2 className="mt-1 text-lg font-semibold text-slate-950">Tijdlijn en lijst per monteur</h2>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="inline-flex rounded-2xl border border-slate-200 bg-slate-50 p-1">
            {[
              { key: "timeline", label: "Tijdlijn" },
              { key: "list", label: "Lijst" },
            ].map((option) => {
              const active = viewMode === option.key;
              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => onViewModeChange?.(option.key)}
                  className={`rounded-xl px-3 py-1.5 text-sm font-medium transition ${
                    active
                      ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-600 hover:text-slate-800"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>

          <input
            type="date"
            value={selectedDate}
            onChange={(e) => onDateChange(e.target.value)}
            className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none transition focus:border-slate-400 focus:bg-white"
          />
        </div>
      </div>

      <div className="border-b border-slate-100 px-4 py-3 text-xs text-slate-500">
        {viewMode === "timeline"
          ? "Sleep een werkbon naar een monteurregel. De planning snapt op blokken van 30 minuten."
          : "Lijstweergave toont dezelfde planning rustiger onder elkaar per monteur."}
      </div>

      <div className="divide-y divide-slate-100">
        {rows.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-slate-500">
            Geen monteurs beschikbaar voor deze planning.
          </div>
        ) : (
          rows.map((row) =>
            viewMode === "timeline" ? (
              <TechnicianRow
                key={row.id}
                row={row}
                hours={hours}
                onOpen={onOpen}
                onDropCard={onDropCard}
                onDragStart={onDragStart}
              />
            ) : (
              <TechnicianListRow
                key={row.id}
                row={row}
                onOpen={onOpen}
                onDragStart={onDragStart}
              />
            )
          )
        )}
      </div>
    </section>
  );
}

function TechnicianListRow({ row, onOpen, onDragStart }) {
  const tone =
    row.conflictCount > 0 || row.capacityTone === "over"
      ? "border-rose-200 bg-rose-50/50"
      : row.capacityTone === "warning"
      ? "border-amber-200 bg-amber-50/40"
      : "border-slate-200 bg-white";

  return (
    <div className={`grid gap-3 px-4 py-4 lg:grid-cols-[240px_minmax(0,1fr)] ${tone}`}>
      <div>
        <p className="text-base font-semibold text-slate-950">{row.name}</p>
        <p className="mt-1 text-sm text-slate-500">{row.capacityLabel}</p>
        <div className="mt-2 flex flex-wrap gap-2 text-[11px] font-semibold">
          <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-slate-700">
            {row.assignmentCount} item(s)
          </span>
          {row.conflictCount > 0 ? (
            <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-1 text-rose-700">
              {row.conflictCount} conflict(en)
            </span>
          ) : null}
        </div>
      </div>

      <div className="space-y-2">
        {row.items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-500">
            Geen geplande items op deze dag.
          </div>
        ) : (
          row.items.map((item) => (
            <button
              key={`${row.id}-${item.id}`}
              type="button"
              draggable
              onDragStart={(event) => onDragStart(event, item, "list")}
              onClick={() => onOpen(item)}
              className={`flex w-full items-start justify-between gap-3 rounded-xl border px-3 py-2.5 text-left transition ${
                item.hasConflict
                  ? "border-rose-200 bg-rose-50/70"
                  : "border-slate-200 bg-white hover:bg-slate-50"
              }`}
            >
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2 text-xs text-slate-500">
                  <span>{item.startTime || "--:--"}</span>
                  <span>•</span>
                  <span>{item.durationLabel || "-"}</span>
                </span>
                <span className="mt-1 block truncate text-sm font-semibold text-slate-900">
                  {item.werkbonNummer || "Werkbon"} • {item.title}
                </span>
                <span className="mt-0.5 flex items-center gap-2 text-xs">
                  <span className={`rounded-full px-2 py-0.5 font-semibold ${item.statusBadgeClass}`}>
                    {item.statusLabel}
                  </span>
                  <span className="truncate text-slate-500">
                    {item.clientName || "-"} • {item.locationLabel || "-"}
                  </span>
                </span>
              </span>

              {item.hasConflict ? (
                <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700">
                  Conflict
                </span>
              ) : null}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
